import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import {
  PollHistoryEntry,
  pollHistoryAtom,
  removeFromHistoryAtom,
} from './pollHistoryAtom';
import { PollStatus } from './types';

interface UsePollHistoryOptions<TData = unknown> {
  requestId: string | null | undefined;
  onCompleted?: (data: TData | null) => void;
  onFailed?: (error?: string) => void;
  onTimeout?: () => void;
  cleanupOnUnmount?: boolean;
}

interface UsePollHistoryResult<TData = unknown> {
  status: PollStatus | null;
  data: TData | null;
  error: string | null;
  isCompleted: boolean;
  isFailed: boolean;
  isTimeout: boolean;
  isPending: boolean;
  cleanup: () => void;
}

function extractPollData<TData>(
  entry: PollHistoryEntry | undefined
): TData | null {
  if (!entry?.poll) return null;

  const pollData = entry.poll?.data;
  if (pollData && typeof pollData === 'object' && 'data' in pollData) {
    return pollData.data as TData;
  }
  return (pollData as TData) ?? null;
}

export function usePollHistory<TData = unknown>({
  requestId,
  onCompleted,
  onFailed,
  onTimeout,
  cleanupOnUnmount = true,
}: UsePollHistoryOptions<TData>): UsePollHistoryResult<TData> {
  const pollHistory = useAtomValue(pollHistoryAtom);
  const removeFromHistory = useSetAtom(removeFromHistoryAtom);
  const callbacksFiredRef = useRef<Set<string>>(new Set());

  const entry = useMemo(() => {
    if (!requestId) return undefined;
    return pollHistory.find((e) => e.poll?.metadata?.requestId === requestId);
  }, [pollHistory, requestId]);

  const status = entry?.status ?? null;
  const data = useMemo(() => extractPollData<TData>(entry), [entry]);
  const error = entry?.error ?? null;

  const isCompleted = status === PollStatus.Completed;
  const isFailed = status === PollStatus.Failed;
  const isTimeout = status === PollStatus.Timeout;
  const isPending =
    status === PollStatus.Pending || status === PollStatus.InProgress;

  const cleanup = useCallback(() => {
    if (requestId) {
      removeFromHistory(requestId);
    }
  }, [requestId, removeFromHistory]);

  // Fire callbacks when status changes (only once per status)
  useEffect(() => {
    if (!requestId || !status) return;

    const callbackKey = `${requestId}-${status}`;
    if (callbacksFiredRef.current.has(callbackKey)) return;

    if (isCompleted && onCompleted) {
      callbacksFiredRef.current.add(callbackKey);
      onCompleted(data);
    } else if (isFailed && onFailed) {
      callbacksFiredRef.current.add(callbackKey);
      onFailed(error ?? undefined);
    } else if (isTimeout && onTimeout) {
      callbacksFiredRef.current.add(callbackKey);
      onTimeout();
    }
  }, [
    requestId,
    status,
    isCompleted,
    isFailed,
    isTimeout,
    data,
    error,
    onCompleted,
    onFailed,
    onTimeout,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cleanupOnUnmount && requestId) {
        removeFromHistory(requestId);
      }
    };
  }, [cleanupOnUnmount, requestId, removeFromHistory]);

  return {
    status,
    data,
    error,
    isCompleted,
    isFailed,
    isTimeout,
    isPending,
    cleanup,
  };
}
